import React, { useContext, useEffect, useRef, useState } from "react";
import { Spinner } from "reactstrap";
import { FaPlus, FaClock, FaRegClock } from "react-icons/fa";
import { IoSettingsOutline } from "react-icons/io5";
import { Language } from "../../App";
import { resolveMentorImage, DEFAULT_MENTOR_AVATAR as placeholderAvatar } from "../../utils/mentorshipUtils";
import { isImageCached, markImageLoaded } from "../../utils/imageCache";

// Same SplashData.user source CreateMentorship.jsx reads from — mentor_profile
// is null before ever applying, and once it exists its `status` tells apart
// a pending application (0) from an approved mentor (1).
function getSplashUser() {
  try {
    return JSON.parse(localStorage.getItem("SplashData"))?.user || null;
  } catch (e) {
    return null;
  }
}

// MentorshipGrid.jsx's /user/mentor/home fetch stashes these doctor-level
// stats here — read them instead of hitting that endpoint again from here.
function getHomeStats() {
  try {
    return JSON.parse(localStorage.getItem("mr_home_data")) || {};
  } catch (e) {
    return {};
  }
}

// "Submitted on" comes from mentor_profile.created_at inside SignupData
// (set on login, response.data straight from the login API) — turned into
// "31 July, 2026" instead of the raw ISO timestamp.
function getMentorApplicationSubmittedOn() {
  try {
    const createdAt = JSON.parse(localStorage.getItem("SignupData"))?.data?.mentor_profile?.created_at;
    if (!createdAt) return null;
    const date = new Date(createdAt);
    if (isNaN(date)) return null;
    return date.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
  } catch (e) {
    return null;
  }
}

function BecomeMentorCard({ onApply, onEdit, onManageProfile, onOpenSettings, mentorApplication }) {
  const { lang } = useContext(Language);
  const splashUser = getSplashUser();
  const mentorProfile = splashUser?.mentor_profile || null;
  const image = resolveMentorImage(
    mentorProfile?.profile_picture || splashUser?.image || mentorApplication?.image
  );
  const [photoLoaded, setPhotoLoaded] = useState(() => isImageCached(image));
  const imgRef = useRef(null);

  useEffect(() => {
    if (isImageCached(image)) {
      setPhotoLoaded(true);
    } else if (imgRef.current?.complete && imgRef.current?.naturalWidth > 0) {
      markImageLoaded(image);
      setPhotoLoaded(true);
    } else {
      setPhotoLoaded(false);
    }
  }, [image]);

  // ---------- CASE 0: Approved mentor (mentor_profile.status === 1) — show
  // their own mentor profile summary instead of the application/CTA states
  // below. ----------
  if (mentorProfile?.status === 1) {
    const homeStats = getHomeStats();
    const name = splashUser?.name || mentorApplication?.name || "Dr. Jane Smith";
    const specialty = mentorProfile.specialty || mentorApplication?.specialty || "Ophthalmology";
    const bio =
      mentorProfile.bio ||
      mentorApplication?.bio ||
      "Cornea and ocular surface specialist. Reference for complex dry eye and surface reconstruction.";

    return (
      <section
        className="w-100 d-flex flex-column align-items-center text-center mb-3 position-relative"
        style={{
          background:
            "radial-gradient(circle at top left, #EAFAFD 0%, #EAFAFD 10%, #FFFFFF 40%, #FFFFFF 100%)",
          border: "1px solid #DCEEF3",
          borderRadius: "20px",
          padding: "24px 20px",
        }}
      >
        <button
          type="button"
          onClick={onOpenSettings}
          className="position-absolute d-flex align-items-center justify-content-center"
          style={{
            top: "14px",
            right: "14px",
            width: "30px",
            height: "30px",
            borderRadius: "50%",
            background: "#EAF9FD",
            border: "1px solid #D7E0EA",

          }}
        >
          <IoSettingsOutline size={18} color="#5F6673" />
        </button>

        {/* Profile Photo */}
        <div
          className="position-relative d-flex align-items-center justify-content-center"
          style={{
            width: "110px",
            height: "110px",
            borderRadius: "20px",
            overflow: "hidden",
            border: "2px solid #fff",
            marginBottom: "16px",
            background: "#F7FDFF",
          }}
        >
          {!photoLoaded && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1,
              }}
            >
              <Spinner style={{ color: "#1dc1dd" }} />
            </div>
          )}
          <img
            ref={imgRef}
            src={image || placeholderAvatar}
            alt={name}
            loading="eager"
            decoding="async"
            onLoad={() => {
              markImageLoaded(image);
              setPhotoLoaded(true);
            }}
            onError={(e) => {
              markImageLoaded(image);
              setPhotoLoaded(true);
              if (e.target.src !== placeholderAvatar) {
                e.target.src = placeholderAvatar;
              }
            }}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              opacity: photoLoaded ? 1 : 0,
              transition: photoLoaded ? "none" : "opacity 0.25s ease-out",
              willChange: "opacity",
            }}
          />
        </div>

        {/* Name */}
        <h6
  className="mb-2 text-truncate"
  style={{
    fontWeight: 700,
    fontSize: "1rem",
    color: "#0F1A2B",
    maxWidth: "100%",
  }}
>
  {name}
</h6>
        {/* Specialty */}
        <span
          className="mb-3"
          style={{
            fontSize: "0.78rem",
            fontWeight: 600,
            color: "#111",
            background: "#E8FBFF",
            borderRadius: "6px",
            padding: "4px 14px",
            display: "inline-block",
          }}
        >
          {specialty}
        </span>

        {/* Stats */}
        <div
          className="d-flex align-items-center w-100 mb-3"
          style={{ borderTop: "1px solid #EAEEF2", borderBottom: "1px solid #EAEEF2", padding: "12px 0" }}
        >
          <div className="flex-fill">
            <p className="mb-0" style={{ fontWeight: 700, fontSize: "16px", color: "#0E1421" }}>
              {homeStats.completed_sessions ?? 0}
            </p>
            <p className="mb-0" style={{ fontSize: "12px", color: "#5F6673", fontWeight:"500" }}>
              {lang?.sessions || "Sessions"}
            </p>
          </div>
          <div style={{ width: "1px", height: "30px", background: "#EAEEF2" }} />
          <div className="flex-fill">
            <p className="mb-0" style={{ fontWeight: 700, fontSize: "16px", color: "#0E1421" }}>
              {homeStats.mentees ?? 0}
            </p>
            <p className="mb-0" style={{ fontSize: "12px", color: "#5F6673", fontWeight:"500" }}>
              {lang?.mentorship_mentees_label || "Mentees"}
            </p>
          </div>
        </div>

        {/* Bio */}
       <p
  className="mb-3"
  style={{
    fontSize: "12px",
    color: "#5F6673",
    lineHeight: "18px",
    fontWeight: 400,
    minWidth: 0,
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  }}
>
  {bio}
</p>

        {/* Manage Mentor Profile */}
        <button
          type="button"
          onClick={onManageProfile}
          className="w-100"
          style={{
            background: "#1DC1DD0A",
            border: "0.5px solid #00BADA",
            borderRadius: "999px",
            padding: "10px 1rem",
            fontWeight: 600,
            fontSize: "12px",
            color: "#0E1421",
            cursor: "pointer",
          }}
        >
          {lang?.mentorship_manage_profile_button || "Manage Mentor Profile"}
        </button>
      </section>
    );
  }

  // ---------- CASE 1: Applied but not yet approved (mentor_profile exists,
  // status !== 1) — show the "under review" status card. ----------
  if (mentorProfile) {
    const image = resolveMentorImage(
      mentorProfile?.profile_picture || splashUser?.image || mentorApplication?.image
    );
    const name = splashUser?.name || mentorApplication?.name || "Dr. Jane Smith";
    const specialty = mentorProfile.specialty || mentorApplication?.specialty || "Ophthalmology";
    const submittedOn = getMentorApplicationSubmittedOn() || mentorApplication?.submittedOn;

    return (
      <section
        className="w-100 d-flex flex-column align-items-center text-center mb-3"
       style={{
  background: "radial-gradient(circle at top left, #EAFAFD 0%, #EAFAFD 10%, #FFFFFF 40%, #FFFFFF 100%)",
  border: "1px solid #DCEEF3",
  borderRadius: "20px",
  padding: "24px 20px",
}}
      >
        {/* Profile Photo */}
        <div
          className="position-relative d-flex align-items-center justify-content-center"
          style={{
            width: "110px",
            height: "110px",
            borderRadius: "20px",
            overflow: "hidden",
            border: "2px solid #fff",
            marginBottom: "16px",
            background: "#F7FDFF",
          }}
        >
          {!photoLoaded && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1,
              }}
            >
              <Spinner style={{ color: "#1dc1dd" }} />
            </div>
          )}
          <img
            ref={imgRef}
            src={image || placeholderAvatar}
            alt={name}
            loading="eager"
            decoding="async"
            onLoad={() => {
              markImageLoaded(image);
              setPhotoLoaded(true);
            }}
            onError={(e) => {
              markImageLoaded(image);
              setPhotoLoaded(true);
              if (e.target.src !== placeholderAvatar) {
                e.target.src = placeholderAvatar;
              }
            }}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              opacity: photoLoaded ? 1 : 0,
              transition: photoLoaded ? "none" : "opacity 0.25s ease-out",
              willChange: "opacity",
            }}
          />
        </div>

        {/* Name */}
        <h6
          className="mb-2"
          style={{ fontWeight: 700, fontSize: "1rem", color: "#0F1A2B" }}
        >
          {name}
        </h6>

        {/* Specialty */}
        <span
          className="mb-3"
          style={{
            fontSize: "0.78rem",
            fontWeight: 600,
            color: "#111",
            background: "#E8FBFF",
            borderRadius: "8px",
            padding: "4px 14px",
            display: "inline-block",
          }}
        >
          {specialty}
        </span>

        {/* Application Status Box */}
        <div
          className="w-100 p-3 mb-3"
          style={{ background: "#FFF8E6", border: "1px solid #FCD34D", borderRadius: "16px" }}
        >
          <div className="d-flex align-items-center justify-content-center gap-1 mb-1">
           <FaRegClock
 size={11} color="#B45309" />
            <span
              style={{
                fontSize: "12px",
                fontWeight: 600,
                color: "#B45309",
                letterSpacing: "0.3px",
              }}
            >
              {lang?.mentorship_application_status_label || "Application Status"}
            </span>
          </div>

          <p
            className="mb-1"
            style={{ fontWeight: 600, fontSize: "14px", color: "#0F1A2B" }}
          >
            {lang?.mentorship_under_review || "Under Review"}
          </p>

          <p
            className="mb-0"
            style={{
              fontSize: "11px",
              color: "#5F6673",
              lineHeight: "18px",
              fontWeight:"400"
            }}
          >
            {lang?.mentorship_under_review_desc || "Your mentor application is under review by our team. We'll notify you once it's approved."}
          </p>
        </div>

        {/* Edit Mentor Application */}
        <button
          type="button"
          onClick={onEdit}
          className="w-100 mb-2"
          style={{
            background: "rgba(29, 193, 221, 0.04)",
            border: "0.5px solid #00BADA",
            borderRadius: "30px",
            padding: "10px 1rem",
            fontWeight: 600,
            fontSize: "12px",
            color: "#0F1A2B",
            cursor: "pointer",
          }}
        >
          {lang?.mentorship_edit_application_button || "Edit Mentor Application"}
        </button>

        {/* Submitted date — from mentor_profile.created_at in SignupData,
            falling back to local state right after submitting this session
            if that's not there yet for some reason. */}
        {submittedOn && (
          <div
            className="d-flex align-items-center justify-content-center gap-1"
            style={{ color: "#9CA3AF", fontSize: "0.72rem" }}
          >
            <FaRegClock size={12} style={{marginBottom:"2px"}} />
            <span>{lang?.mentorship_submitted_on_prefix || "Submitted on"} {submittedOn}</span>
          </div>
        )}
      </section>
    );
  }

  // ---------- CASE 2: Not applied yet — show "Become a Mentor" CTA ----------
  return (
    <section
      className="w-100 rounded-4 shadow-sm p-4 d-flex flex-column align-items-start justify-content-start gap-2 text-start mb-3 position-relative overflow-hidden"
      style={{ background: "#061223" }}
    >
      {/* Decorative glow — pinned to the top-right corner via `right` (not a
          fixed `left` px, which assumed the ~335px desktop sidebar width and
          landed in the wrong spot on the full-width mobile card) */}
      <div
        style={{
          position: "absolute",
          width: "100px",
          height: "100px",
          top: "-17px",
          right: "-20px",
          background: "#00BADA",
          opacity: "40%",
          filter: "blur(100px)",
          borderRadius: "50%",
          pointerEvents: "none",
        }}
      />

      <span
        className="fs-12 roboto-regular mb-1"
        style={{ color: "#1DC1DD", letterSpacing: "0.5px", position: "relative" }}
      >
        {lang?.mentorship_for_specialists_label || "FOR SPECIALISTS"}
      </span>
      <h5 className="roboto-bold fs-16 text-white mb-1" style={{ position: "relative" }}>
        {lang?.mentorship_become_a_mentor_title || "Become a Mentor"}
      </h5>

      <p
        className="fs-12 mb-3"
        style={{ color: "#FFFFFFB2", fontWeight: "400", lineHeight: "20px", position: "relative" }}
      >
        {lang?.mentorship_become_mentor_cta_desc || "Share your expertise with young ophthalmologists and guide the next generation of eye-care specialists."}
      </p>

      <button
        type="button"
        onClick={onApply}
        className="w-100 d-flex align-items-center justify-content-center gap-2 text-white py-2 fs-12"
        style={{ background: "#1DC1DD", position: "relative", borderRadius: "16px", fontWeight: "700", border: "none" }}
      >
        <FaPlus size={11} /> {lang?.mentorship_apply_become_mentor_button || "Apply to become a Mentor"}
      </button>
    </section>
  );
}

export default React.memo(BecomeMentorCard);
